import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import * as Query from '../../../../api/gql/query'
import { useGanttStore } from '../../store/ganttStore'
import type { ScheduleActivityLogEntry } from '../../types'
import { describeMeta } from '../../components/ActivityLogDialog'

const TAKE = 10

interface Props {
  scheduleId: string
  activityId: string
  onShowAll: () => void
}

/**
 * Recent audit trail for the focused activity (Task 17, spec B §9.7 step 6) — the last 10 entries,
 * reusing `ActivityLogDialog`'s `describeMeta` for the details column. `cache-and-network` shows the
 * cached list instantly (switching activities is frequent) while still checking for newer entries.
 */
const HistorySection: React.FC<Props> = ({ scheduleId, activityId, onShowAll }) => {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(Query.SCHEDULE_ACTIVITY_LOG, {
    variables: { scheduleId, activityId, take: TAKE },
    fetchPolicy: 'cache-and-network',
  })
  const entries: ScheduleActivityLogEntry[] = data?.scheduleActivityLog ?? []

  // Review fix (Minor #15): the log used to refetch only on `activityId` change, so editing a
  // field in the panel above didn't show its new log entry until the user re-focused the row.
  // Refetching when the mutation queue drains (`pendingCount` 0 -> back to 0) catches it as soon
  // as the write settles, without polling.
  const pendingCount = useGanttStore((s) => s.pendingCount)
  const wasPending = useRef(pendingCount > 0)
  useEffect(() => {
    if (wasPending.current && pendingCount === 0) refetch().catch(() => undefined)
    wasPending.current = pendingCount > 0
  }, [pendingCount, refetch])

  return (
    <div>
      {loading && entries.length === 0 && <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground mx-auto my-2" />}
      {!loading && entries.length === 0 && <p className="text-xs text-muted-foreground">{t('Schedule_log_empty')}</p>}
      <ul className="space-y-1.5">
        {entries.map((e) => {
          const details = describeMeta(e, t)
          return (
            <li key={e.id} className="text-xs">
              <div className="text-muted-foreground tabular-nums">
                {new Date(e.createdAt).toLocaleString(t('date_locale') || 'en-US')} · {e.user?.name ?? '—'}
              </div>
              <div>{t(`Schedule_log_${e.action}`)}{details ? ` — ${details}` : ''}</div>
            </li>
          )
        })}
      </ul>
      <Button variant="link" size="sm" className="h-auto p-0 mt-1 text-xs" onClick={onShowAll}>{t('Schedule_show_all')}</Button>
    </div>
  )
}

export default HistorySection
